import { rem } from 'polished'
import React, { useCallback } from 'react'
import { Controller, useController, useFormContext } from 'react-hook-form'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'

import AddressDisplay from './Fields/AddressDisplay'
import {
  priceFieldProps,
  priceRegisterConfig,
  required,
  urlRegisterConfig,
} from './Fields/config'
import ConfirmedBooking from './Fields/ConfirmedBooking'
import RHFDatePickerInput from './Fields/RHFDatePickerInput'
import Input from './Fields/RHFInput'
import TextArea from './Fields/RHFTextArea'
import ShowMore from './Fields/ShowMore'
import TimeInput from './Fields/TimeInput'
import { useDefaultDatePickerMonthYear } from './Hooks/useDefaultDates'
import useEndPlaceIsSame from './Hooks/useEndPlaceIsSame'
import useEnsureGeoPlace from './Hooks/useEnsureGeoPlace'
import type { CreateTourItemViewModel } from '../Create/models'
import type { PatchTourItemViewModel } from '../Edit/models'

import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Divider from 'LuxKit/Divider'
import LineMapMarkerIcon from 'LuxKit/Icons/line/LineMapMarkerIcon'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import Group from 'components/utils/Group'
import type {
  TripAddressAutocompleteOption,
} from 'tripPlanner/components/AutocompleteInputs/TripAddressAutocomplete'
import TripAddressAutocomplete from 'tripPlanner/components/AutocompleteInputs/TripAddressAutocomplete'
import type { AutocompletePlaceParam } from 'tripPlanner/types/google'
import {
  ADDRESS_RESPONSE_DETAILS,
} from 'tripPlanner/types/google'
import { convertGoogleAddressComponentsToRegion } from 'tripPlanner/utils'

const PriceInput = styled(Input)`
  width: ${rem(100)};
`

const EndPlaceContainer = styled.div`
  padding-bottom: ${rem(8)};
`

const DividerContainer = styled.div`
  height: ${rem(24)};
`

const StyledVerticalSpacer = styled(VerticalSpacer)`
  margin-top: ${rem(20)};
  margin-bottom: ${rem(20)};
`

const AUTOCOMPLETE_TYPES: AutocompletePlaceParam = []

const START_GEO_DEP_KEYS: Array<string> = ['startPlace.name']
const END_GEO_DEP_KEYS: Array<string> = ['endPlace.name']

interface Props {
  tripId: string
}

function TourFields({ tripId }: Props) {
  const intl = useIntl()
  const methods = useFormContext<
    CreateTourItemViewModel | PatchTourItemViewModel
  >()
  const { watch, control, setValue, register, clearErrors } = methods

  const { defaultMonth, defaultYear } = useDefaultDatePickerMonthYear({
    tripId,
  })

  useEnsureGeoPlace(methods, 'startPlace', START_GEO_DEP_KEYS)
  useEnsureGeoPlace(methods, 'endPlace', END_GEO_DEP_KEYS)

  const startPlace = watch('startPlace')
  const endPlace = watch('endPlace')

  useEndPlaceIsSame(methods)

  const handleAddressChange = useCallback(
    (
      placeName: 'startPlace' | 'endPlace',
      selectedOption: TripAddressAutocompleteOption,
    ) => {
      setValue(
        `${placeName}.name`,
        selectedOption.details?.name || selectedOption.label,
      )

      setValue(
        `${placeName}.type`,
        selectedOption.prediction ? 'GEO' : 'CUSTOM',
      )

      if (selectedOption.prediction) {
        const { place_id } = selectedOption.prediction
        setValue(`${placeName}.id`, place_id)
      }

      if (selectedOption.details) {
        const { geometry, formatted_address, website, address_components } =
          selectedOption.details

        if (address_components) {
          const region =
            convertGoogleAddressComponentsToRegion(address_components)
          if (region) {
            setValue(`${placeName}.region`, region)
          }
        }

        if (website) {
          setValue('url', website)
        }
        formatted_address && setValue(`${placeName}.address`, formatted_address)

        if (geometry?.location) {
          setValue(`${placeName}.point.lat`, geometry.location.lat)
          setValue(`${placeName}.point.lng`, geometry.location.lng)
        }
      }
    },
    [setValue],
  )

  const handleStartAddressChange = useCallback(
    (selectedOption: TripAddressAutocompleteOption) => {
      handleAddressChange('startPlace', selectedOption)
    },
    [handleAddressChange],
  )

  const handleEndAddressChange = useCallback(
    (selectedOption: TripAddressAutocompleteOption) => {
      handleAddressChange('endPlace', selectedOption)
    },
    [handleAddressChange],
  )

  const {
    fieldState: { error: endPlaceError },
  } = useController({ name: 'endPlace' })

  const handleSetStartPlaceToEndPlace = useCallback(() => {
    setValue('endPlace', startPlace)

    // React hook form not recognizing valid field after this update
    if (endPlaceError) {
      clearErrors('endPlace')
    }
  }, [setValue, startPlace, clearErrors, endPlaceError])

  const tourNameLabel = intl.formatMessage({
    id: '4RRJB/s66h',
    defaultMessage: 'Tour name',
    description: 'Form label for tour name field in tour trip item editor',
  })

  const tourNamePlaceholder = intl.formatMessage({
    id: 'W7XUwGdU9Q',
    defaultMessage: 'Enter a tour name',
    description: 'Placeholder text for tour name input in tour trip item editor',
  })

  const pickUpDetailsHeading = intl.formatMessage({
    id: 'vUqND+AZ8T',
    defaultMessage: 'Pick up details',
    description: 'Section heading for pick up details in tour trip item editor',
  })

  const pickUpLocationLabel = intl.formatMessage({
    id: 'lbBsMqcFRX',
    defaultMessage: 'Pick up location',
    description: 'Form label for pick up location field in tour trip item editor',
  })

  const searchLocationPlaceholder = intl.formatMessage({
    id: 'lKuVnJ2KlY',
    defaultMessage: 'Search for location',
    description: 'Placeholder text for location search input in tour trip item editor',
  })

  const addressLabel = intl.formatMessage({
    id: 'M+dRC5nfJI',
    defaultMessage: 'Address',
    description: 'Label for address display in tour trip item',
  })

  const pickUpDateLabel = intl.formatMessage({
    id: 'xJcIRhmv12',
    defaultMessage: 'Pick up date',
    description: 'Form label for pick up date field in tour trip item editor',
  })

  const timeLabel = intl.formatMessage({
    id: '3FlWDeAZUj',
    defaultMessage: 'Time',
    description: 'Form label for time field in tour trip item editor',
  })

  const selectTimePlaceholder = intl.formatMessage({
    id: 'sew6Y8JTI8',
    defaultMessage: 'Select time',
    description: 'Placeholder text for time picker in tour trip item editor',
  })

  const dropOffDetailsHeading = intl.formatMessage({
    id: 'BQOYt/vixM',
    defaultMessage: 'Drop off details',
    description: 'Section heading for drop off details in tour trip item editor',
  })

  const dropOffLocationLabel = intl.formatMessage({
    id: 'YUIIOvNzA0',
    defaultMessage: 'Drop-off location',
    description: 'Form label for drop-off location field in tour trip item editor',
  })

  const dropOffDateLabel = intl.formatMessage({
    id: '42xUSdcZ7a',
    defaultMessage: 'Drop-off date',
    description: 'Form label for drop-off date field in tour trip item editor',
  })

  const operatorLabel = intl.formatMessage({
    id: 'rPZWmf/F3s',
    defaultMessage: 'Operator',
    description: 'Form label for tour operator field in tour trip item editor',
  })

  const operatorPlaceholder = intl.formatMessage({
    id: 'yrWMk0LZ7A',
    defaultMessage: 'Tour operator',
    description: 'Placeholder text for tour operator input in tour trip item editor',
  })

  const priceLabel = intl.formatMessage({
    id: 'YYXdwvzfIy',
    defaultMessage: 'Price',
    description: 'Form label for price field in tour trip item editor',
  })

  const websiteLabel = intl.formatMessage({
    id: 'KVIPKcVguf',
    defaultMessage: 'Website',
    description: 'Form label for website URL field in tour trip item editor',
  })

  const noteLabel = intl.formatMessage({
    id: 'PZGMhcU1oB',
    defaultMessage: 'Note',
    description: 'Form label for notes field in tour trip item editor',
  })

  const copyAddressHelpText = <BodyText variant="medium">
    <FormattedMessage
      id="SQo3hEYHqK"
      defaultMessage="Add same address as <link>pick up location</link>"
      description="Link to copy pick up address to drop off address in tour form"
      values={{
        link: (chunks) => <TextLink onClick={handleSetStartPlaceToEndPlace} underline={false}>{chunks}</TextLink>,
      }}
    />
  </BodyText>

  return (
    <StyledVerticalSpacer gap={16}>
      <Input
        {...register('name', { required })}
        required
        label={tourNameLabel}
        placeholder={tourNamePlaceholder}
      />

      <Heading variant="heading4">{pickUpDetailsHeading}</Heading>

      <TripAddressAutocomplete
        {...register('startPlace.name', { required })}
        required
        label={pickUpLocationLabel}
        placeholder={searchLocationPlaceholder}
        initPlaceId={startPlace?.type === 'GEO' ? startPlace.id : undefined}
        autocompleteTypes={AUTOCOMPLETE_TYPES}
        details={ADDRESS_RESPONSE_DETAILS}
        onPlaceSelect={handleStartAddressChange}
        startIcon={<LineMapMarkerIcon />}
      />
      {startPlace?.address && (
        <AddressDisplay title={addressLabel} value={startPlace.address} />
      )}

      <Group direction="horizontal" horizontalAlign="stretch" gap={16}>
        <RHFDatePickerInput
          name="startDate"
          label={pickUpDateLabel}
          defaultMonth={defaultMonth}
          defaultYear={defaultYear}
        />
        <TimeInput
          {...register('startTime')}
          label={timeLabel}
          placeholder={selectTimePlaceholder}
        />
      </Group>

      <DividerContainer>
        <Divider kind="primary" />
      </DividerContainer>

      <Heading variant="heading4">{dropOffDetailsHeading}</Heading>

      <EndPlaceContainer>
        <TripAddressAutocomplete
          {...register('endPlace.name', { required })}
          required
          label={dropOffLocationLabel}
          placeholder={searchLocationPlaceholder}
          initPlaceId={endPlace?.type === 'GEO' ? endPlace.id : undefined}
          autocompleteTypes={AUTOCOMPLETE_TYPES}
          details={ADDRESS_RESPONSE_DETAILS}
          onPlaceSelect={handleEndAddressChange}
          startIcon={<LineMapMarkerIcon />}
          helpText={copyAddressHelpText}
        />
        {endPlaceError && copyAddressHelpText}
      </EndPlaceContainer>

      {endPlace?.address && (
        <AddressDisplay title={addressLabel} value={endPlace.address} />
      )}

      <Group direction="horizontal" horizontalAlign="stretch" gap={16}>
        <RHFDatePickerInput
          name="endDate"
          label={dropOffDateLabel}
          defaultMonth={defaultMonth}
          defaultYear={defaultYear}
        />
        <TimeInput
          {...register('endTime')}
          label={timeLabel}
          placeholder={selectTimePlaceholder}
        />
      </Group>

      <DividerContainer>
        <Divider kind="primary" />
      </DividerContainer>

      <Controller
        name="isBooked"
        control={control}
        render={({ field }) => (
          <ConfirmedBooking
            toggled={!!field.value}
            onToggle={() => field.onChange(!field.value)}
          />
        )}
      />

      <ShowMore>
        <Input
          {...register('operatorName')}
          label={operatorLabel}
          placeholder={operatorPlaceholder}
        />

        <PriceInput
          {...register('price', { ...priceRegisterConfig })}
          label={priceLabel}
          placeholder="0.00"
          {...priceFieldProps}
        />

        <Input
          {...register('url', { ...urlRegisterConfig })}
          label={websiteLabel}
          placeholder="https://"
        />

        <TextArea
          {...register('notes')}
          placeholder="Add note here"
          label={noteLabel}
          size="tall"
          resize="vertical"
        />
      </ShowMore>
    </StyledVerticalSpacer>
  )
}

export default TourFields
